import {
  ChangeEventHandler,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useToast } from '~/common/providers/toast';
import { useAuth } from '~/common/providers/auth';
import { IFCWithChildren } from '~/common/interfaces/i-fc-with-childrent';
import { useDebounce } from '~/common/hooks/use-debounce';
import { useCookies } from '~/common/hooks/use-cookies';
import { UserSubscriptionTypesEnum } from '~/common/enums/user-subscription-types.enum';
import { cookies } from '~/common/constants/cookies';
import { api } from '~/common/api';

import { normalizePromptMediaFields } from '../utils/normalize-prompt-media';
import { IPromptsStatistics } from '../interfaces/i-prompts-statistics';
import { IPromptRequest } from '../interfaces/i-prompt-request';
import { IPromptListResponse } from '../interfaces/i-prompt-list-response';
import { IPromptListItem } from '../interfaces/i-prompt-list-item';
import { PromptTypesEnum } from '../enums/prompt-types-enum';

export type TSortTypes = 'most-used' | 'newest' | 'likes';

interface IPersistedPromptListFilters {
  category: PromptTypesEnum | null;
  search: string;
  tier: UserSubscriptionTypesEnum | null;
  sortBy: TSortTypes;
  advancedFilters: Record<string, string[]>;
}

interface IPromptListRestriction {
  active: boolean;
  unlocksAt?: string | Date | null;
  visiblePercentage?: number;
}

const DEFAULT_SORT_BY: TSortTypes = 'most-used';
const DEFAULT_TIER = 'all' as UserSubscriptionTypesEnum;

const defaultPersistedFilters = (): IPersistedPromptListFilters => ({
  category: null,
  search: '',
  tier: DEFAULT_TIER,
  sortBy: DEFAULT_SORT_BY,
  advancedFilters: {},
});

const parsePersistedFilters = (value?: string): IPersistedPromptListFilters => {
  if (!value) {
    return defaultPersistedFilters();
  }

  try {
    const parsed = JSON.parse(value) as Partial<IPersistedPromptListFilters>;

    return {
      category:
        parsed.category === undefined
          ? defaultPersistedFilters().category
          : parsed.category,
      search:
        typeof parsed.search === 'string'
          ? parsed.search
          : defaultPersistedFilters().search,
      tier:
        parsed.tier === undefined
          ? defaultPersistedFilters().tier
          : parsed.tier,
      sortBy:
        parsed.sortBy === 'most-used' ||
        parsed.sortBy === 'newest' ||
        parsed.sortBy === 'likes'
          ? parsed.sortBy
          : defaultPersistedFilters().sortBy,
      advancedFilters:
        parsed.advancedFilters && typeof parsed.advancedFilters === 'object'
          ? parsed.advancedFilters
          : defaultPersistedFilters().advancedFilters,
    };
  } catch {
    return defaultPersistedFilters();
  }
};

const hasActiveFilters = (filters: IPersistedPromptListFilters): boolean => {
  const hasAdvanced = Object.values(filters.advancedFilters).some(
    (options) => options.length > 0
  );

  return Boolean(
    filters.category ||
      filters.search ||
      (filters.tier && filters.tier !== DEFAULT_TIER) ||
      filters.sortBy !== DEFAULT_SORT_BY ||
      hasAdvanced
  );
};

interface IPromptListContextData {
  prompts: IPromptListItem[];
  loading: boolean;
  restriction: IPromptListRestriction | null;
  search: string;
  sortBy: TSortTypes;
  category: PromptTypesEnum;
  advancedFilters: Record<string, string[]>;
  tier: UserSubscriptionTypesEnum;
  hasMorePromptsToLoad: boolean;
  hasLoadedFirstCall: boolean;
  statistics: IPromptsStatistics;
  handleChangeSortBy: (sortBy: TSortTypes) => void;
  refflectLikeAction: (liking: boolean) => void;
  loadAtBottom: () => Promise<void>;
  debounceSearchChange: ChangeEventHandler<HTMLInputElement>;
  changeTier: (tier: UserSubscriptionTypesEnum) => void;
  unapprovePrompt: (promptId: string) => Promise<void>;
  approvePrompt: (promptId: string) => Promise<void>;
  clearAdvancedFilters: () => void;
  handleAdvancedFilterSelect: (
    subcategoryId: string,
    optionValue: string
  ) => void;
  handleCategoryChange: (newCategory: PromptTypesEnum) => void;
  handleDeletePrompt: (promptId: string) => Promise<void>;
  handleSavePrompt: (
    prompt: IPromptRequest,
    updatingId?: string
  ) => Promise<void>;
}

const PromptListContext = createContext({} as IPromptListContextData);
const PromptListProvider: IFCWithChildren = ({ children }) => {
  const { showToast } = useToast();
  const { getItem, setItem, removeItem } = useCookies();
  const persistedFilters = useMemo<IPersistedPromptListFilters>(
    () => parsePersistedFilters(getItem(cookies.promptListFilters)),
    [getItem]
  );
  const { user: { type: userType = '', canApprovePrompts } = {} } = useAuth();
  const [loading, setLoading] = useState(true);
  const [prompts, setPrompts] = useState<IPromptListItem[]>([]);
  const [restriction, setRestriction] = useState<IPromptListRestriction | null>(
    null
  );
  const [category, setCategory] = useState<PromptTypesEnum>(
    persistedFilters.category as PromptTypesEnum
  );
  const [search, setSearch] = useState(persistedFilters.search);
  const [tier, setTier] = useState<UserSubscriptionTypesEnum>(
    persistedFilters.tier as UserSubscriptionTypesEnum
  );
  const [sortBy, setSortBy] = useState<TSortTypes>(persistedFilters.sortBy);
  const [statistics, setStatistics] = useState<IPromptsStatistics>();
  const [advancedFilters, setAdvancedFilters] = useState<
    Record<string, string[]>
  >(persistedFilters.advancedFilters);

  const hasLoadedFirstCall = useRef<boolean>(false);
  const hasMorePromptsToLoad = useRef<boolean>(true);
  const filtersRef = useRef<Record<string, unknown>>({
    offset: 0,
    category: persistedFilters.category,
    search: persistedFilters.search,
    tier: persistedFilters.tier,
    sortBy: persistedFilters.sortBy,
    advancedFilters: persistedFilters.advancedFilters,
  });

  const persistFilters = useCallback(
    (filters: IPersistedPromptListFilters) => {
      if (!hasActiveFilters(filters)) {
        removeItem(cookies.promptListFilters);
        return;
      }

      setItem(cookies.promptListFilters, JSON.stringify(filters));
    },
    [removeItem, setItem]
  );

  const approvePrompt = useCallback(
    async (promptId: string) => {
      if (userType.toLowerCase() !== 'admin') {
        return;
      }

      try {
        await api.post(`/prompts/${promptId}/approve`);
        showToast({
          severity: 'success',
          title: 'Sucesso',
          description: 'Prompt aprovado com sucesso.',
        });

        setPrompts((currentPrompts) =>
          currentPrompts.map((prompt) =>
            String(prompt.id) === String(promptId)
              ? { ...prompt, approved: true }
              : prompt
          )
        );
      } catch {
        showToast({
          severity: 'error',
          title: 'Erro',
          description: 'Não foi possível aprovar o prompt.',
        });
      }
    },
    [showToast, userType]
  );

  const unapprovePrompt = useCallback(
    async (promptId: string) => {
      if (userType.toLowerCase() !== 'admin') {
        return;
      }

      try {
        await api.post(`/prompts/${promptId}/unapprove`);
        showToast({
          severity: 'success',
          title: 'Sucesso',
          description: 'Prompt desaprovado com sucesso.',
        });

        setPrompts((currentPrompts) =>
          currentPrompts.map((prompt) =>
            prompt.id === promptId ? { ...prompt, approved: false } : prompt
          )
        );
      } catch {
        showToast({
          severity: 'error',
          title: 'Erro',
          description: 'Não foi possível desaprovar o prompt.',
        });
      }
    },
    [showToast, userType]
  );

  const load = useCallback(
    async (params: Record<string, unknown>, reset: boolean) => {
      if (reset) {
        hasMorePromptsToLoad.current = true;
      }

      try {
        const route =
          userType.toLowerCase() === 'admin'
            ? '/prompts/admin/list-all'
            : '/prompts';

        const arrayToCommaSeparated = (arr: string[]): string =>
          arr.map((str) => str.trim()).join(',');

        const paramsFinal = {
          ...params,
          category: params.category === 'all' ? null : params.category,
          tiers: params.tier === 'all' ? null : params.tier,
          subcategories: params.advancedFilters
            ? arrayToCommaSeparated(
                Object.values(params.advancedFilters).reduce((acc, values) => {
                  acc.push(...values);
                  return acc;
                }, [])
              )
            : [],
        };

        delete paramsFinal['advancedFilters'];
        const {
          data: {
            data: response,
            hasMore,
            restrictionActive,
            restrictionUnlocksAt,
            restrictionVisiblePercentage,
          },
        } = await api.get<IPromptListResponse>(route, {
          params: paramsFinal,
        });

        setRestriction(
          restrictionActive
            ? {
                active: true,
                unlocksAt: restrictionUnlocksAt,
                visiblePercentage: restrictionVisiblePercentage,
              }
            : null
        );

        hasMorePromptsToLoad.current = hasMore;
        if (!hasLoadedFirstCall.current) {
          hasLoadedFirstCall.current = true;
        }

        const newOffset = response.length + (params.offset as number);
        filtersRef.current = {
          ...filtersRef.current,
          offset: newOffset,
        };

        let toSet = response.map((prompt) =>
          normalizePromptMediaFields(prompt, true)
        );

        if (userType.toLowerCase() === 'admin' && canApprovePrompts) {
          toSet = toSet.sort((a, b) => {
            if (a.approved === b.approved) {
              return 0;
            }
            return a.approved ? 1 : -1;
          });
        }

        if (reset) {
          setPrompts(toSet);
        } else {
          setPrompts((prev) => [...(prev || []), ...toSet]);
        }
      } catch {
        showToast({
          severity: 'error',
          title: 'Erro',
          description: 'Não foi possível carregar os prompts.',
        });
      } finally {
        setLoading(false);
      }
    },
    [userType, canApprovePrompts, showToast]
  );

  const loadAtBottom = useCallback(async () => {
    if (!hasMorePromptsToLoad.current) {
      return;
    }

    await load(filtersRef.current, false);
  }, [load]);

  const handleChangeSortBy = useCallback(
    (newSortBy: TSortTypes) => {
      setSortBy(newSortBy);
      filtersRef.current = {
        ...filtersRef.current,
        sortBy: newSortBy,
        offset: 0,
      };

      persistFilters({
        category: category || null,
        search,
        tier: tier || null,
        sortBy: newSortBy,
        advancedFilters,
      });

      load(filtersRef.current, true);
    },
    [advancedFilters, category, load, persistFilters, search, tier]
  );

  const refflectLikeAction = useCallback((liking: boolean) => {
    setStatistics((prev) => ({
      ...prev,
      totalLikes: liking
        ? (prev?.totalLikes || 0) + 1
        : (prev?.totalLikes || 0) - 1,
    }));
  }, []);

  const changeTier = useCallback(
    (newTier: UserSubscriptionTypesEnum) => {
      setTier((prev) => {
        if (prev === newTier) {
          return prev;
        }

        const toSet = prev === newTier ? undefined : newTier;
        filtersRef.current = {
          ...filtersRef.current,
          tier: toSet,
          offset: 0,
        };

        persistFilters({
          category: category || null,
          search,
          tier: toSet || null,
          sortBy,
          advancedFilters,
        });

        load(filtersRef.current, true);
        return toSet;
      });
    },
    [advancedFilters, category, load, persistFilters, search, sortBy]
  );

  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const { value } = event.target;
      setSearch(value);
      filtersRef.current = {
        ...filtersRef.current,
        search: value,
        offset: 0,
      };

      persistFilters({
        category: category || null,
        search: value,
        tier: tier || null,
        sortBy,
        advancedFilters,
      });

      load(filtersRef.current, true);
    },
    [advancedFilters, category, load, persistFilters, sortBy, tier]
  );

  const debounceSearchChange = useDebounce(handleSearchChange, 1000);

  const clearAdvancedFilters = useCallback(() => {
    setAdvancedFilters({});
    filtersRef.current = {
      ...filtersRef.current,
      advancedFilters: {},
      offset: 0,
    };

    persistFilters({
      category: category || null,
      search,
      tier: tier || null,
      sortBy,
      advancedFilters: {},
    });

    load(filtersRef.current, true);
  }, [category, load, persistFilters, search, sortBy, tier]);

  const handleAdvancedFilterSelect = useCallback(
    (subcategoryId: string, optionValue: string) => {
      setAdvancedFilters((prev) => {
        const currentOptions = prev[subcategoryId] || [];
        let updatedOptions: string[];

        if (currentOptions.includes(optionValue)) {
          updatedOptions = currentOptions.filter((opt) => opt !== optionValue);
        } else {
          updatedOptions = [...currentOptions, optionValue];
        }

        const returning = {
          ...prev,
          [subcategoryId]: updatedOptions,
        };

        filtersRef.current = {
          ...filtersRef.current,
          advancedFilters: returning,
          offset: 0,
        };

        persistFilters({
          category: category || null,
          search,
          tier: tier || null,
          sortBy,
          advancedFilters: returning,
        });

        load(filtersRef.current, true);
        return returning;
      });
    },
    [category, load, persistFilters, search, sortBy, tier]
  );

  const handleCategoryChange = useCallback(
    (newCategory: PromptTypesEnum) => {
      if (newCategory === category) {
        return;
      }

      setCategory(newCategory);
      setAdvancedFilters({});
      filtersRef.current = {
        ...filtersRef.current,
        category: newCategory,
        advancedFilters: {},
        offset: 0,
      };

      persistFilters({
        category: newCategory,
        search,
        tier: tier || null,
        sortBy,
        advancedFilters: {},
      });

      load(filtersRef.current, true);
    },
    [category, load, persistFilters, search, sortBy, tier]
  );

  const handleSavePrompt = useCallback(
    async (prompt: IPromptRequest, updatingId?: string) => {
      try {
        const method = updatingId ? 'patch' : 'post';
        const path = updatingId ? `/prompts/${updatingId}` : '/prompts';
        await api[method](path, prompt);

        showToast({
          severity: 'success',
          title: 'Sucesso',
          description: `Prompt ${
            updatingId ? 'atualizado' : 'criado'
          } com sucesso.`,
        });

        // clear all filters
        setCategory(undefined);
        setTier(undefined);
        setAdvancedFilters({});
        setSearch('');
        setSortBy(DEFAULT_SORT_BY);
        filtersRef.current = {
          offset: 0,
        };

        removeItem(cookies.promptListFilters);

        load(filtersRef.current, true);
      } catch {
        showToast({
          severity: 'error',
          title: 'Erro',
          description: 'Não foi possível salvar o prompt.',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [load, removeItem, showToast, userType]
  );

  const handleDeletePrompt = useCallback(
    async (promptId: string) => {
      try {
        await api.delete(`/prompts/${promptId}`);
        setPrompts((currentPrompts) =>
          currentPrompts.filter((prompt) => prompt.id !== promptId)
        );

        showToast({
          severity: 'success',
          title: 'Sucesso',
          description: 'Prompt deletado com sucesso.',
        });
      } catch {
        showToast({
          severity: 'error',
          title: 'Erro',
          description: 'Não foi possível deletar o prompt.',
        });
      }
    },
    [showToast]
  );

  useEffect(() => {
    load(filtersRef.current, true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  return (
    <PromptListContext.Provider
      value={{
        loading,
        prompts,
        restriction,
        search,
        category,
        advancedFilters,
        tier,
        statistics,
        hasMorePromptsToLoad: hasMorePromptsToLoad.current,
        hasLoadedFirstCall: hasLoadedFirstCall.current,
        sortBy,
        handleChangeSortBy,
        changeTier,
        refflectLikeAction,
        clearAdvancedFilters,
        handleAdvancedFilterSelect,
        handleCategoryChange,
        debounceSearchChange,
        handleDeletePrompt,
        handleSavePrompt,
        unapprovePrompt,
        loadAtBottom,
        approvePrompt,
      }}
    >
      {children}
    </PromptListContext.Provider>
  );
};

const usePromptList = (): IPromptListContextData => {
  return useContext(PromptListContext);
};

export { PromptListProvider, usePromptList };
